import React, {useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect, useDispatch} from 'react-redux';
import {bindActionCreators} from 'redux';

import {ProtocolEditorContext} from '../../ContextProviders/ProtocolEditorContext';

import {find} from '../../../../lib/objectHelper';
import withIntl from '../../../../lib/withIntl';

import WizardHeader from '../../../../components/WizardHeader/WizardHeader';
import {TaskType, ProtocolEditorViewMode, Feedback} from '../../../../constants/dataEnum';

import {showSnackbar} from '../../../../stores/actions/appStateAction';
import {showModal, hideModal} from '../../../../stores/actions/modalStateAction';
import {
  poolingBackHandler,
  cancelHandler,
  getPoolingStateBeforeEdit,
  onFinishPoolingWizard,
} from '../../../../stores/business/taskListPoolingBusiness';
import {globalAddUndoItem} from '../../../../stores/business/undoRedoBusinessAction';
import {
  setPoolingTaskEditStep,
  setPoolingTaskStep,
  setSelectedMenu,
} from '../../../../stores/actions/protocolEditorAction';

const mapStateToProps = (state) => {
  const {poolingTaskEditStep, poolingTaskStep} = state.protocolEditorState;
  return {
    cardStateCards: state.cardState.cards,
    cardStateTasks: state.cardState.tasks,
    protocolPoolingTaskEditStep: poolingTaskEditStep,
    protocolPoolingTaskStep: poolingTaskStep,
  };
};

const mapDispatchToProps = (dispatch) => ({
  appStateActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  modalStateActionShowModal: bindActionCreators(showModal, dispatch),
  modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
  taskListPoolingBusinessBackHandler: bindActionCreators(poolingBackHandler, dispatch),
  taskListPoolingBusinessCancelHandler: bindActionCreators(cancelHandler, dispatch),
  undoRedoBusinessActionGlobalAddUndoItem: bindActionCreators(globalAddUndoItem, dispatch),
  taskListPoolingBusinessGetPoolingStateBeforeEdit: bindActionCreators(getPoolingStateBeforeEdit, dispatch),
  protocolEditorStateActionSetPoolingTaskEditStep: bindActionCreators(setPoolingTaskEditStep, dispatch),
  protocolEditorStateActionSetPoolingTaskStep: bindActionCreators(setPoolingTaskStep, dispatch),
  setProtocolEditorMode: bindActionCreators(setSelectedMenu, dispatch),
});

function PoolingHeader({
  intl,
  cardStateCards,
  cardStateTasks,
  appStateActionShowSnackbar,
  modalStateActionShowModal,
  modalStateActionHideModal,
  taskListPoolingBusinessBackHandler,
  taskListPoolingBusinessCancelHandler,
  undoRedoBusinessActionGlobalAddUndoItem,
  notifyBack,
  notifyCancel,
  notifyCancelEdit,
  notifyNext,
  headerRef,
  taskListPoolingBusinessGetPoolingStateBeforeEdit,
  protocolPoolingTaskEditStep,
  protocolEditorStateActionSetPoolingTaskEditStep,
  protocolPoolingTaskStep,
  protocolEditorStateActionSetPoolingTaskStep,
  setProtocolEditorMode,
}) {
  const {poolingSetAllowedTips} = useContext(ProtocolEditorContext);

  const isEdit = !!protocolPoolingTaskEditStep;
  const selectedCard = find(cardStateCards, {isSelected: true});
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
  const dispatch = useDispatch();

  const getTaskDispensesPooling = () => {
    const currentSelectedCard = selectedCard;

    if (!currentSelectedCard) {
      return [];
    }

    const allTasks = cardStateTasks ?? [];

    const task = find(allTasks, {ID: currentSelectedCard.tasks, type: TaskType.DISPENSE});

    return task;
  };

  const getPoolingTasks = () => {
    const currentSelectedCard = selectedCard;

    if (!currentSelectedCard) {
      return [];
    }

    const allTasks = cardStateTasks ?? [];

    const poolingTask = find(allTasks, {ID: currentSelectedCard.tasks});
    if (poolingTask) {
      return [poolingTask];
    }

    return [];
  };

  const isIncompleteCard = () => {
    const taskDispense = getTaskDispensesPooling();

    return !taskDispense || !taskDispense.volume;
  };

  useEffect(() => {
    const isIncomplete = isIncompleteCard();
    if (isIncomplete !== isNextButtonDisabled) {
      setIsNextButtonDisabled(isIncomplete);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [cardStateTasks]);

  const hideBackButton = () => !getPoolingTasks().length || isEdit;
  const hideNextButton = () => !isEdit;

  const onBack = () => {
    taskListPoolingBusinessBackHandler();
    notifyBack();
  };

  const onNext = async () => {
    const editTaskSuccessMessage = intl.formatMessage({id: 'protocolEditor.pooling.editTask.successMessage'});

    dispatch(onFinishPoolingWizard(selectedCard.ID));
    appStateActionShowSnackbar(Feedback.SUCCESS, editTaskSuccessMessage);
    notifyNext();

    if (isEdit && protocolPoolingTaskEditStep === 1) {
      await poolingSetAllowedTips();
    }

    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const onCancel = async () => {
    const stateBeforeEdit = taskListPoolingBusinessGetPoolingStateBeforeEdit();
    if (!selectedCard || isEdit || stateBeforeEdit) {
      if (!selectedCard || ((isEdit || stateBeforeEdit) && !isIncompleteCard())) {
        await setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
      }

      if ((isEdit || stateBeforeEdit) && isIncompleteCard()) {
        await setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
        notifyCancelEdit();
      } else {
        notifyCancel();
      }

      await protocolEditorStateActionSetPoolingTaskEditStep(null);
      await protocolEditorStateActionSetPoolingTaskStep(null);
      undoRedoBusinessActionGlobalAddUndoItem();

      return;
    }

    const title = intl.formatMessage({id: 'protocolEditor.pooling.cancelModal.title'});
    const text = intl.formatMessage({id: 'protocolEditor.pooling.cancelModal.text'});
    const okButtonText = intl.formatMessage({id: 'protocolEditor.pooling.cancelModal.okButton'});
    const cancelButtonText = intl.formatMessage({id: 'protocolEditor.pooling.cancelModal.cancelButton'});
    const cancelSuccessMessage = intl.formatMessage({id: 'protocolEditor.pooling.cancelModal.successMessage'});

    const modalOkHandler = () => {
      taskListPoolingBusinessCancelHandler();
      setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
      protocolEditorStateActionSetPoolingTaskEditStep(null);
      protocolEditorStateActionSetPoolingTaskStep(null);
      modalStateActionHideModal();
      appStateActionShowSnackbar(Feedback.SUCCESS, cancelSuccessMessage);
      notifyCancel();
    };

    const modalCancelHandler = () => {
      setProtocolEditorMode(ProtocolEditorViewMode.POOLING);
      modalStateActionHideModal();
    };

    modalStateActionShowModal(
      title,
      text,
      [
        {className: 'btn-lg', style: 'primary', handlerClick: modalOkHandler, text: okButtonText},
        {className: 'btn-lg', style: 'secondary', handlerClick: modalCancelHandler, text: cancelButtonText},
      ],
      modalCancelHandler,
      '',
      true,
    );
  };

  const renderText = () => {
    let text = '';
    if (protocolPoolingTaskStep) {
      switch (protocolPoolingTaskStep) {
        case 1:
        // eslint-disable-next-line default-case-last, no-fallthrough
        default:
          text = 'Pooling Guide - Click or swipe to define your pooling source well(s)';
          break;
        case 2:
          text = 'Pooling Guide - Input aspirate volume from each source well';
          break;
        case 3:
          text = 'Pooling Guide - Click to define your well(s) destination';
          break;
      }
    } else {
      switch (protocolPoolingTaskEditStep) {
        case 1:
        // eslint-disable-next-line default-case-last, no-fallthrough
        default:
          text = 'Pooling Guide - Click or swipe to define your pooling source well(s)';
          break;
        case 2:
          text = 'Pooling Guide - Input aspirate volume from each source well';
          break;
        case 3:
          text = 'Pooling Guide - Click to define your well(s) destination';
          break;
      }
    }

    return (
      <span className="header-text">
        <span className="header-text-title">{text}</span>
      </span>
    );
  };

  return (
    <div ref={headerRef}>
      <WizardHeader
        hideBackButton={hideBackButton()}
        hideNextButton={hideNextButton()}
        nextButtonText="APPLY"
        cancelButtonText="CANCEL"
        onBackClick={onBack}
        onNextClick={onNext}
        onCancelClick={onCancel}
        disableNextButton={isNextButtonDisabled}
      >
        {renderText()}
      </WizardHeader>
    </div>
  );
}

PoolingHeader.propTypes = {
  intl: PropTypes.object.isRequired,
  cardStateCards: PropTypes.object.isRequired,
  cardStateTasks: PropTypes.object.isRequired,
  appStateActionShowSnackbar: PropTypes.func.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  taskListPoolingBusinessBackHandler: PropTypes.func.isRequired,
  taskListPoolingBusinessCancelHandler: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalAddUndoItem: PropTypes.func.isRequired,
  notifyBack: PropTypes.func,
  notifyCancel: PropTypes.func,
  notifyCancelEdit: PropTypes.func,
  notifyNext: PropTypes.func,
  headerRef: PropTypes.object,
  taskListPoolingBusinessGetPoolingStateBeforeEdit: PropTypes.func.isRequired,
  protocolPoolingTaskEditStep: PropTypes.any,
  protocolEditorStateActionSetPoolingTaskEditStep: PropTypes.func.isRequired,
  protocolPoolingTaskStep: PropTypes.any,
  protocolEditorStateActionSetPoolingTaskStep: PropTypes.func.isRequired,
  setProtocolEditorMode: PropTypes.func.isRequired,
};

PoolingHeader.defaultProps = {
  notifyBack: () => {},
  notifyCancel: () => {},
  notifyCancelEdit: () => {},
  notifyNext: () => {},
  headerRef: null,
  protocolPoolingTaskStep: null,
  protocolPoolingTaskEditStep: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(PoolingHeader));
